import type { MessageDescriptor } from 'react-intl';

export interface CreateAppExample {
  readonly id: string;
  readonly name: MessageDescriptor;
  readonly brief: MessageDescriptor;
}

export const CREATE_APP_EXAMPLES: readonly CreateAppExample[] = [
  {
    id: 'rattan-vase',
    name: { id: 'apps.builder.create.example.rattanVase.name', defaultMessage: 'Rattan Vase' },
    brief: {
      id: 'apps.builder.create.example.rattanVase.brief',
      defaultMessage:
        'Build an app that turns a plain vase into a rattan-style vase for large-format additive manufacturing. ' +
        'The user uploads the vase as an STL or STEP file and sets the weave density, the strand thickness in ' +
        'millimetres and the offset of the shell from the vase surface. The run wraps the vase in a woven, ' +
        'rattan-like lattice shell and delivers it as an STL. Show the result in the 3D viewer with a download button.',
    },
  },
  {
    id: 'mould-designer',
    name: { id: 'apps.builder.create.example.mouldDesigner.name', defaultMessage: 'Mould Designer' },
    brief: {
      id: 'apps.builder.create.example.mouldDesigner.brief',
      defaultMessage:
        'Build a mould designer. The user uploads a part as a STEP file and sets the mould wall thickness in ' +
        'millimetres and the draft angle in degrees. The run builds a two-part mould block around the part, split ' +
        'at a parting plane through its widest section, with a pouring sprue and four bolt holes in the corners, ' +
        'and delivers each half as its own STEP file. Show both halves in the 3D viewer with a download for each.',
    },
  },
  {
    id: 'room-designer',
    name: { id: 'apps.builder.create.example.roomDesigner.name', defaultMessage: 'Room Designer' },
    brief: {
      id: 'apps.builder.create.example.roomDesigner.brief',
      defaultMessage:
        'Build a room designer. The user describes the room in the chat (what it is for, its size and the style ' +
        'they want) and can upload an empty room shell as a STEP file. The run furnishes the room with simple, ' +
        'true-to-scale furniture and delivers the scene as a GLB plus a rendered image of it. Show the scene in ' +
        'the 3D viewer and the render beside it, and let the user ask for changes such as "swap the sofa for two armchairs".',
    },
  },
  {
    id: 'bracket-configurator',
    name: { id: 'apps.builder.create.example.bracketConfigurator.name', defaultMessage: 'Bracket configurator' },
    brief: {
      id: 'apps.builder.create.example.bracketConfigurator.brief',
      defaultMessage:
        'Build an L-bracket configurator that works entirely in the browser, with no chat and no runs. The user ' +
        'sets the width, depth, height and thickness in millimetres with sliders and the number of mounting holes ' +
        'with a select, and the bracket rebuilds live in the 3D viewer as the values change. An Export button ' +
        'downloads the bracket as a STEP file, with STL as a second option.',
    },
  },
  {
    id: 'part-report',
    name: { id: 'apps.builder.create.example.partReport.name', defaultMessage: 'Part report' },
    brief: {
      id: 'apps.builder.create.example.partReport.brief',
      defaultMessage:
        'Build a part report app. The user uploads one STEP file and presses Analyse. The run computes the ' +
        "part's volume, its mass in aluminium, its surface area and its bounding box, and writes them into a " +
        'one-page PDF report. The page shows the numbers in a table, the bounding box as a bar chart and the ' +
        'PDF report with a download button.',
    },
  },
];

export const exampleTileImage = (id: string, light: boolean): string =>
  `/create-app-examples/${id}-${light ? 'light' : 'dark'}.webp`;
